import type { TranslationResponse } from '@/types';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface DetectedLanguageInfo {
  code: string;
  name: string;
  confidence: number;
}

export interface TranslateTabState {
  sourceText: string;
  sourceLanguage: string;
  targetLanguages: string[];
  isTranslating: boolean;
  translationResult: TranslationResponse | null;
  detectedLanguageInfo: DetectedLanguageInfo | null;
  isDetecting: boolean;
  enableAiBoost: boolean;
  llmProvider: string;
  fileMode: boolean;
}

export const initialTranslateTabState: TranslateTabState = {
  sourceText: '',
  sourceLanguage: 'auto',
  targetLanguages: [],
  isTranslating: false,
  translationResult: null,
  detectedLanguageInfo: null,
  isDetecting: false,
  enableAiBoost: false,
  llmProvider: 'openrouter',
  fileMode: false,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type TranslateTabAction =
  | { type: 'SET_SOURCE_TEXT'; payload: string }
  | { type: 'SET_SOURCE_LANGUAGE'; payload: string }
  | { type: 'SET_TARGET_LANGUAGES'; payload: string[] }
  | { type: 'SET_IS_TRANSLATING'; payload: boolean }
  | { type: 'SET_TRANSLATION_RESULT'; payload: TranslationResponse | null }
  | { type: 'SET_DETECTED_LANGUAGE_INFO'; payload: DetectedLanguageInfo | null }
  | { type: 'SET_IS_DETECTING'; payload: boolean }
  | { type: 'SET_ENABLE_AI_BOOST'; payload: boolean }
  | { type: 'SET_LLM_PROVIDER'; payload: string }
  | { type: 'SET_FILE_MODE'; payload: boolean }
  | {
      type: 'SWAP_LANGUAGES';
      payload: {
        newSourceText: string;
        newSourceLang: string;
        newTargetLang: string;
      };
    }
  | { type: 'RESET' };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function translateTabReducer(
  state: TranslateTabState,
  action: TranslateTabAction
): TranslateTabState {
  switch (action.type) {
    case 'SET_SOURCE_TEXT':
      return { ...state, sourceText: action.payload };

    case 'SET_SOURCE_LANGUAGE':
      return { ...state, sourceLanguage: action.payload };

    case 'SET_TARGET_LANGUAGES':
      return { ...state, targetLanguages: action.payload };

    case 'SET_IS_TRANSLATING':
      return { ...state, isTranslating: action.payload };

    case 'SET_TRANSLATION_RESULT':
      return { ...state, translationResult: action.payload };

    case 'SET_DETECTED_LANGUAGE_INFO':
      return { ...state, detectedLanguageInfo: action.payload };

    case 'SET_IS_DETECTING':
      return { ...state, isDetecting: action.payload };

    case 'SET_ENABLE_AI_BOOST':
      return { ...state, enableAiBoost: action.payload };

    case 'SET_LLM_PROVIDER':
      return { ...state, llmProvider: action.payload };

    case 'SET_FILE_MODE':
      return { ...state, fileMode: action.payload };

    case 'SWAP_LANGUAGES':
      return {
        ...state,
        sourceText: action.payload.newSourceText,
        sourceLanguage: action.payload.newSourceLang,
        targetLanguages: [action.payload.newTargetLang],
        translationResult: null,
        detectedLanguageInfo: null,
      };

    case 'RESET':
      return initialTranslateTabState;

    default:
      return state;
  }
}
